import { service } from '@ember/service';

import type { RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import type { ValidationRule } from 'webapp/utils/validate';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type { SpaceBillingInfo, SpaceDataset, SpaceDatasource } from 'webapp/models/space';
import type SpaceRecord from 'webapp/models/space';
import type UserRecord from 'webapp/models/user';

import type Account from 'webapp/services/account';
import type Constants from 'webapp/services/constants';
import type CurrentUser from 'webapp/services/current-user';

export interface Metadata {
	industries: string[];
	businessClassifications: string[];
	employees: string | null;
	revenue: string | null;
	sourcesSelection: string[];
	cloudSourcesOpportunity: string[];
	goal?: string | null;
}

export const PREDEFINED_BUSINESS_CLASSIFICATOR_FOR_AGENCY = 'Agency';

export default class Space extends BufferedModel<SpaceRecord, RecordData.Space.Serialized> {
	@service declare account: Account;
	@service declare accountProgress: Service.AccountProgress;
	@service declare constants: Constants;
	@service declare currentUser: CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get activeUntil(): number | null {
		return this.model.activeUntil;
	}

	get accountType(): number {
		return this.model.accountType;
	}

	get companyName(): string {
		return this.getBuffered('companyName');
	}

	set companyName(value: string) {
		this.setBuffered('companyName', value);
	}

	get websiteUrl(): string {
		return this.getBuffered('websiteUrl');
	}

	set websiteUrl(value: string) {
		this.setBuffered('websiteUrl', value);
	}

	get metadata(): Metadata {
		return this.getBuffered('metadata');
	}

	set metadata(value: Metadata) {
		this.setBuffered('metadata', value);
	}

	get loginUrl(): string {
		return `/change/space/${this.id}`;
	}

	get isEnterprise(): boolean {
		return this.getBuffered('isEnterprise');
	}

	set isEnterprise(value: boolean) {
		this.setBuffered('isEnterprise', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdByName(): string {
		return this.model.createdByName;
	}

	get color(): string {
		return this.model.color || '#8E929B'; // @gray_500
	}

	get boardCount(): number {
		return this.model.boardCount;
	}

	get sourceCount(): number {
		return this.model.sourceCount;
	}

	get datasetCount(): number {
		return this.model.datasetCount;
	}

	get userCount(): number {
		return this.model.userCount;
	}

	get customMetricCount(): number {
		return this.model.customMetricCount;
	}

	get reportCount(): number {
		return this.model.reportCount;
	}

	get datasources(): SpaceDatasource[] {
		return this.model.datasources;
	}

	get datasets(): SpaceDataset[] {
		return this.model.datasets;
	}

	get ownerId(): number {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number) {
		this.setBuffered('ownerId', value);
	}

	get accountManagerId(): number {
		return this.getBuffered('accountManagerId');
	}

	get adminName(): string {
		return this.getBuffered('adminName');
	}

	set adminName(value: string) {
		this.setBuffered('adminName', value);
	}

	get adminEmail(): string {
		return this.model.adminEmail;
	}

	set adminEmail(value: string) {
		this.setBuffered('adminEmail', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get stepsCompleted(): number[] {
		return this.model.stepsCompleted;
	}

	get hidden(): boolean {
		return this.model.hidden;
	}

	get billingInfo(): SpaceBillingInfo {
		return this.model.billingInfo;
	}

	get parentResellerId(): number | null {
		return this.model.parentResellerId;
	}

	// ------- other props ------

	get text() {
		return this.companyName; // for SearchableList, to satisfy PopupListItem
	}

	override get validationRules(): Record<string, ValidationRule[]> {
		const fields: Record<string, ValidationRule[]> = {
			companyName: ['required'],
		};

		if (this.websiteUrl) {
			fields.websiteUrl = ['url'];
		}

		return fields;
	}

	get img() {
		// to show avatars, must not be undefined (for popup-spaces/searchable-list)
		return '';
	}

	get isSelected(): boolean {
		return this.id === String(this.account.id);
	}

	get isOwnedByMe(): boolean {
		return this.ownerId === this.currentUser.numericId!;
	}

	get accountManager(): UserRecord | undefined {
		let user;
		if (this.accountManagerId) {
			user = this.mmUsersService.activatedUserRecords.find(
				(item) => item.id === String(this.accountManagerId),
			);
		}
		return user;
	}

	get accountOwner(): UserRecord | undefined {
		let user;
		if (this.accountManagerId) {
			user = this.mmUsersService.activatedUserRecords.find(
				(item) => item.id === String(this.ownerId),
			);
		}
		return user;
	}

	get tagsObjs() {
		const tagIds = this.tags || [];
		return this.mmTagsService.savedRecords.filter((tag) => {
			return tagIds.includes(+tag.id!);
		});
	}

	get accountProgressPoints(): number {
		const progressData = this.constants.accountProgress[String(this.accountType)],
			stageData = progressData?.stages.find((item) => item.id === 1); // only for clients - and clients have only one stage
		return stageData
			? this.accountProgress.getPointsForStage(stageData, this.stepsCompleted)
			: 0;
	}

	get tagsCount(): number {
		return this.tags.length;
	}

	static override modelName = ModelName.SPACE; // store record type

	static isMetadataEmpty(metadata: Metadata) {
		const { industries, businessClassifications, employees, revenue } = metadata;

		return Object.values({ industries, businessClassifications, employees, revenue }).every(
			(value) =>
				!value?.length || value.includes(PREDEFINED_BUSINESS_CLASSIFICATOR_FOR_AGENCY),
		);
	}
}
